import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, MapPin, LogOut } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/ui-bits";
import { useLang } from "@/lib/i18n";

export const Account = () => {
  const navigate = useNavigate();
  const { user, setUser, logout, refreshUser } = useAuth();
  const { t } = useLang();
  const EMPTY = { label: t("checkout.homeLabel"), name: "", street: "", house_number: "", postal_code: "", city: "", country: "Nederland", phone: "", is_default: false };
  const [name, setName] = useState("");
  const [pw, setPw] = useState({ current: "", next: "" });
  const [profileMsg, setProfileMsg] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [addrMsg, setAddrMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user?.name]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg("");
    setError("");
    try {
      const d = await api.put("/auth/profile", {
        name,
        current_password: pw.current,
        new_password: pw.next,
      });
      setUser(d.data);
      setProfileMsg(t("account.saved"));
      setPw({ current: "", next: "" });
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const submitAddress = async (e) => {
    e.preventDefault();
    setAddrMsg("");
    setError("");
    try {
      await api.post("/auth/addresses", form);
      await refreshUser();
      setShowForm(false);
      setForm(EMPTY);
      setAddrMsg(t("account.added"));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const deleteAddress = async (id) => {
    await api.delete(`/auth/addresses/${id}`);
    await refreshUser();
  };

  const field = (key, label, testid, extra = {}) => (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke block mb-2">{label}</span>
      <input
        data-testid={testid}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full bg-obsidian border border-gold/15 px-4 py-3 text-sm text-cream focus:outline-none focus:border-gold/50"
        {...extra}
      />
    </label>
  );

  return (
    <div className="px-6 sm:px-12 lg:px-20 pt-32 pb-24">
      <PageHeader eyebrow={`${t("account.hello")} ${user?.name || ""}`} title={<>{t("account.profile")} <span className="italic normal-case text-gold">{t("account.title2")}</span></>} />

      <div className="grid lg:grid-cols-2 gap-12">
        <section className="border border-gold/15 bg-ink p-8">
          <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-6">{t("account.profile")}</h2>
          <form data-testid="profile-form" onSubmit={saveProfile} className="space-y-5">
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke block mb-2">{t("account.name")}</span>
              <input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2}
                className="w-full bg-obsidian border border-gold/15 px-4 py-3 text-sm text-cream focus:outline-none focus:border-gold/50" />
            </label>
            <div>
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke block mb-2">{t("account.email")}</span>
              <p data-testid="profile-email" className="text-sm text-cream bg-obsidian border border-gold/10 px-4 py-3 opacity-60">{user?.email}</p>
            </div>
            {user?.password_hash ? (
              <div className="border-t border-gold/10 pt-5 space-y-4">
                <label className="block">
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke block mb-2">{t("account.curPw")}</span>
                  <input data-testid="profile-current-password" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })}
                    className="w-full bg-obsidian border border-gold/15 px-4 py-3 text-sm text-cream focus:outline-none focus:border-gold/50" />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke block mb-2">{t("account.newPw")}</span>
                  <input data-testid="profile-new-password" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
                    className="w-full bg-obsidian border border-gold/15 px-4 py-3 text-sm text-cream focus:outline-none focus:border-gold/50" />
                </label>
              </div>
            ) : (
              <p className="font-mono text-[10px] text-smoke uppercase tracking-[0.15em]">{t("account.googleNoPw")}</p>
            )}
            {profileMsg && <p data-testid="profile-success" className="text-xs text-emerald-300">{profileMsg}</p>}
            {error && <p data-testid="profile-error" className="text-xs text-red-400">{error}</p>}
            <button type="submit" data-testid="profile-save-button" className="bg-gold text-obsidian font-mono text-[10px] tracking-[0.3em] uppercase px-8 py-3.5 hover:bg-gold-light transition-colors">
              {t("account.save")}
            </button>
          </form>
          <button
            data-testid="account-logout-button"
            onClick={async () => { await logout(); navigate("/"); }}
            className="mt-8 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-mist hover:text-red-300 transition-colors"
          >
            <LogOut size={13} /> {t("account.logout")}
          </button>
        </section>

        <section className="border border-gold/15 bg-ink p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85">{t("account.addressBook")}</h2>
            <button data-testid="account-address-toggle" onClick={() => setShowForm((s) => !s)} className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-gold hover:text-gold-light transition-colors">
              <Plus size={13} /> {t("account.new")}
            </button>
          </div>
          {addrMsg && <p className="text-xs text-emerald-300 mb-4">{addrMsg}</p>}
          {showForm && (
            <form data-testid="account-address-form" onSubmit={submitAddress} className="grid sm:grid-cols-2 gap-4 border border-gold/20 bg-obsidian p-5 mb-6">
              {field("label", t("account.label"), "addr-label")}
              {field("name", t("account.name"), "addr-name")}
              {field("street", t("checkout.fields.street"), "addr-street")}
              {field("house_number", t("checkout.fields.number"), "addr-number")}
              {field("postal_code", t("checkout.fields.postal"), "addr-postal")}
              {field("city", t("checkout.fields.city"), "addr-city")}
              <label className="flex items-center gap-2.5 sm:col-span-2">
                <input data-testid="addr-default-toggle" type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="h-4 w-4 accent-gold" />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-mist">{t("account.std")}</span>
              </label>
              <button type="submit" data-testid="addr-save-button" className="sm:col-span-2 bg-gold text-obsidian font-mono text-[10px] tracking-[0.3em] uppercase py-3.5">{t("checkout.fields.save")}</button>
            </form>
          )}
          <div className="space-y-3">
            {(user?.addresses || []).length === 0 && (
              <p className="text-sm text-mist flex items-center gap-2"><MapPin size={14} className="text-smoke" /> {t("account.noAddress")}</p>
            )}
            {(user?.addresses || []).map((a) => (
              <div key={a.id} data-testid={`address-card-${a.id}`} className="flex items-start justify-between gap-4 border border-gold/10 bg-obsidian p-5">
                <div>
                  <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-gold/80">{a.label}{a.is_default ? ` · ${t("checkout.default")}` : ""}</p>
                  <p className="mt-2 text-sm text-cream">{a.name}</p>
                  <p className="text-sm text-mist">{a.street} {a.house_number}, {a.postal_code} {a.city}, {a.country}</p>
                </div>
                <button data-testid={`address-delete-${a.id}`} onClick={() => deleteAddress(a.id)} aria-label="Adres verwijderen" className="text-smoke hover:text-red-400 transition-colors">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Account;
